/* eslint-disable complexity */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { isEmpty, times } from 'lodash';
import clsx from 'clsx';
import { Option, Select } from 'carbon-react/lib/components/select';
import { Checkbox } from 'carbon-react/lib/components/checkbox';
import Box from 'carbon-react/lib/components/box';

import { DeepNonNullable, Omit } from '@futrli/shared-services';
import { UICarbonNumberField, UILoaderSpinner } from '@futrli/ui-components';
import { FormattedMessage, IntlShape, useIntl } from '@futrli/localisation';

import { Account, Accounts, PredictSettings } from '../../../core/types';
import {
  PredictManagementContext,
  usePredictManagementContext
} from '../../../predict-management/context/predict-management-context';
import { fetchSettings as fetchSettingsFromApi } from '../../../core/api/settings';
import { fetchAccounts as fetchAccountsFromApi } from '../../../core/api/accounts';
import * as styles from './settings.scss';
import { trackCTA, trackEvent } from '../../../core/tracker';
import {
  DEFAULT_VAT_PAYMENT_DAYS,
  DEFAULT_VAT_RATE,
  DEFAULT_VAT_SCHEME,
  VAT_PERIOD,
  QUARTERLY_MONTH,
  MAX_VAT_PAYMENT_DAYS_MONTHLY,
  VAT_SCHEME,
  MONTHS_IN_YEAR,
  MAX_PERCENTAGE,
  TRACKING_EVENTS
} from '../../../core/constants';
import { formatTAXName } from '../../../core/formatters';
import { updateSettings as updateSettingsToApi } from '../../../core/api/settings';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';
import { AssistantSidePanelFormContainer } from '../assistant-side-panle-form-container';

interface ErrorState {
  [key: string]: string;
}

interface PredictSettingsState extends Omit<DeepNonNullable<PredictSettings>, 'vat_account'> {
  vat_account: Account | null;
}

interface Props {
  onClose: () => void;
}

// return month number for end of next quarterly period
const getDefaultVatPeriodMonth = () =>
  Math.ceil((new Date(Date.now()).getMonth() + 1) / QUARTERLY_MONTH) * QUARTERLY_MONTH;

const getMissingTaxRateMessage = (taxName: string, intl: IntlShape) =>
  intl.formatMessage({ id: 'predict.must_enter_rate', defaultMessage: 'Must enter {taxName} rate' }, { taxName });

const getMissingTaxPeriodDaysMessage = (intl: IntlShape) =>
  intl.formatMessage({ id: 'predict.must_enter_tax_period_days', defaultMessage: 'Must enter number of days' });

export const Settings = ({ onClose }: Props) => {
  const { isFrenchAccounting } = usePredictManagementContext();

  const [isLoading, setIsLoading] = useState(true);
  const [predictSettings, setPredictSettings] = React.useState<PredictSettingsState>();
  const [accounts, setAccounts] = useState<Accounts>();
  const [errors, setErrors] = React.useState<ErrorState | undefined>();
  const [showVatPeriodDaysValidationMsg, setShowVatPeriodDaysValidationMsg] = React.useState(false);

  const { addErrorMessage, currency } = useContext(PredictManagementContext);
  const { fetchForecastReport, fetchForecast, forecast } = usePredictionsInfoContext();

  const { organisationId: tempOrganisationId, workspaceId: tempWorkspaceId } = useParams();

  const organisationId = tempOrganisationId || '';
  const workspaceId = tempWorkspaceId || '';

  const intl = useIntl();

  const missingAccountMessageStr = useMemo(
    () => intl.formatMessage({ id: 'predict.must_select_account', defaultMessage: 'Must select an account' }),
    [intl]
  );
  const duplicateAccountMessageStr = useMemo(
    () =>
      intl.formatMessage({ id: 'predict.must_select_unique_account', defaultMessage: 'Must select a unique account' }),
    [intl]
  );

  if (!forecast) {
    throw new Error('Settings must have a forecast');
  }
  const forecastId = forecast.id;

  // useEffect(() => {
  //   trackViewModal('Predict Settings Modal');
  // }, []); // eslint-disable-line

  useEffect(() => {
    const fetchAccounts = async (): Promise<void> => {
      try {
        const accounts = await fetchAccountsFromApi(workspaceId, organisationId, forecastId);
        setAccounts(accounts);
      } catch {
        addErrorMessage?.(new Error('Failure to get accounts from api'));
      }
    };

    const fetchSettings = async (): Promise<void> => {
      try {
        const fetchedPredictSettings = await fetchSettingsFromApi(workspaceId, organisationId, forecastId);

        setPredictSettings({
          ...fetchedPredictSettings,
          vat_period_days: fetchedPredictSettings.vat_period_days ?? DEFAULT_VAT_PAYMENT_DAYS,
          vat_period: fetchedPredictSettings.vat_period ?? VAT_PERIOD.QUARTERLY,
          vat_period_end_month: fetchedPredictSettings.vat_period_end_month ?? getDefaultVatPeriodMonth(),
          vat_rate: fetchedPredictSettings.vat_rate ?? DEFAULT_VAT_RATE,
          vat_scheme: fetchedPredictSettings.vat_scheme ?? DEFAULT_VAT_SCHEME
        });
      } catch {
        addErrorMessage?.(new Error('Failure to get settings from api'));
      }
    };

    const fetchAccountsAndSettings = async () => {
      try {
        setIsLoading(true);
        await fetchAccounts();
        await fetchSettings();
        setIsLoading(false);
      } catch (e) {
        addErrorMessage?.(new Error('Failure to fetch data'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccountsAndSettings();
  }, [organisationId, workspaceId, forecastId]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasError = (fieldName: keyof PredictSettings): boolean => {
    return (errors && errors[fieldName]) !== undefined;
  };

  const getError = (fieldName: keyof PredictSettings): string | null => {
    return (errors && errors[fieldName]) || null;
  };

  const validate = (predictSettingsToValidate: PredictSettingsState): ErrorState => {
    const errorState: ErrorState = {};

    if (!predictSettingsToValidate?.bank_account) {
      errorState.bank_account = missingAccountMessageStr;
    }

    if (!predictSettingsToValidate?.accounts_receivable_account) {
      errorState.accounts_receivable_account = missingAccountMessageStr;
    }
    if (!predictSettingsToValidate?.accounts_payable_account) {
      errorState.accounts_payable_account = missingAccountMessageStr;
    }
    if (!predictSettingsToValidate?.vat_disabled && !predictSettingsToValidate?.vat_account) {
      errorState.vat_account = missingAccountMessageStr;
    }
    if (
      !predictSettingsToValidate?.vat_disabled &&
      predictSettingsToValidate?.vat_account &&
      predictSettingsToValidate?.accounts_payable_account &&
      predictSettingsToValidate?.vat_account.id === predictSettingsToValidate?.accounts_payable_account.id
    ) {
      errorState.accounts_payable_account = duplicateAccountMessageStr;
      errorState.vat_account = duplicateAccountMessageStr;
    }
    if (!predictSettingsToValidate?.vat_disabled && !predictSettingsToValidate?.vat_rate) {
      errorState.vat_rate = getMissingTaxRateMessage(formatTAXName(currency || undefined, intl), intl);
    }

    if (!predictSettingsToValidate?.vat_disabled && !predictSettingsToValidate?.vat_period_days) {
      errorState.vat_period_days = getMissingTaxPeriodDaysMessage(intl);
    }

    return errorState;
  };

  const handleOnAccountChange = (name: keyof PredictSettings, accountList: Account[]) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const selectedAccount = accountList.find(account => account.id === event.target.value);
    const newPredictSettings = { ...predictSettings, [name]: selectedAccount } as PredictSettingsState;
    setErrors(validate(newPredictSettings));
    setPredictSettings(newPredictSettings);
  };

  const handleOnChange = (name: keyof PredictSettings) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const newPredictSettings = { ...predictSettings, [name]: event.target.value } as PredictSettingsState;

    if (
      name === 'vat_period' &&
      event.target.value === VAT_PERIOD.MONTHLY &&
      typeof newPredictSettings.vat_period_days === 'number' &&
      newPredictSettings.vat_period_days > MAX_VAT_PAYMENT_DAYS_MONTHLY
    ) {
      newPredictSettings.vat_period_days = MAX_VAT_PAYMENT_DAYS_MONTHLY;
      setShowVatPeriodDaysValidationMsg(true);
    } else {
      setShowVatPeriodDaysValidationMsg(false);
    }
    setErrors(validate(newPredictSettings));
    setPredictSettings(newPredictSettings);
  };

  const handleOnNumberChange = (name: keyof PredictSettings) => (value: string): void => {
    const newPredictSettings = { ...predictSettings, [name]: value } as PredictSettingsState;
    setErrors(validate(newPredictSettings));
    setPredictSettings(newPredictSettings);
  };

  const handleOnNumberBlur = (name: keyof PredictSettings, defaultValue: string) => (value: string): void => {
    const parsedValue = value === '' ? defaultValue : value;

    const newPredictSettings = { ...predictSettings, [name]: parsedValue } as PredictSettingsState;
    setErrors(validate(newPredictSettings));
    setPredictSettings(newPredictSettings);
  };

  const handleBooleanChange = (_event: React.ChangeEvent<HTMLInputElement>, name: keyof PredictSettings) => {
    const newPredictSettings = { ...predictSettings, [name]: !predictSettings?.[name] } as PredictSettingsState;
    setErrors(validate(newPredictSettings));
    setPredictSettings(newPredictSettings);
  };

  const onSubmit = async () => {
    trackCTA('Update Settings', 'Update Settings');

    setIsLoading(true);
    try {
      await updateSettingsToApi(predictSettings as PredictSettings, workspaceId, organisationId, forecastId);
      trackEvent(TRACKING_EVENTS.SAVED_FORECAST_SETTINGS);
      fetchForecastReport?.('all');
      fetchForecast?.();
    } catch {
      addErrorMessage?.(new Error('Failure to update settings to api'));
    }
    setIsLoading(false);
    onClose();
  };

  const isVatPeriodMonthly = () => {
    return VAT_PERIOD.MONTHLY === predictSettings?.vat_period;
  };

  if (isLoading) {
    return (
      <Box m={4}>
        <UILoaderSpinner />
      </Box>
    );
  }

  if (!predictSettings || !accounts) {
    return null;
  }

  const submitDisabled = !isEmpty(errors);

  return (
    <AssistantSidePanelFormContainer
      onClose={onClose}
      onSubmit={onSubmit}
      submitDisabled={submitDisabled}
      closeLabel={<FormattedMessage id="common.close" defaultMessage="Close" />}
      submitLabel={<FormattedMessage id="common.update" defaultMessage="Update" />}
    >
      <div>
        <div className={clsx(styles.explainLabel, styles.darkColorFont)} style={{ marginBottom: 16 }}>
          <FormattedMessage
            id="predict.tell_us_how_you_organize_accounts"
            defaultMessage="Tell us how you organize your accounts, this will allow better, more accurate predictions."
          />
        </div>
        <Select
          id="main_bank_account"
          onChange={handleOnAccountChange('bank_account', accounts.bank_account)}
          value={predictSettings.bank_account?.id}
          error={hasError('bank_account') ? getError('bank_account') || undefined : undefined}
          label={intl.formatMessage({ id: 'predict.main_bank_account', defaultMessage: 'Main bank account' })}
          size="large"
          mb={2}
        >
          {accounts.bank_account.map(acc => (
            <Option key={acc.id} value={acc.id} text={acc.name} />
          ))}
        </Select>
        <Select
          id="accounts_receivable_account"
          onChange={handleOnAccountChange('accounts_receivable_account', accounts.accounts_receivable_account)}
          value={predictSettings.accounts_receivable_account?.id}
          error={
            hasError('accounts_receivable_account') ? getError('accounts_receivable_account') || undefined : undefined
          }
          label={intl.formatMessage({
            id: 'predict.accounts_receivable_account',
            defaultMessage: 'Accounts receivable account'
          })}
          size="large"
          mb={2}
        >
          {accounts.accounts_receivable_account.map(acc => (
            <Option key={acc.id} value={acc.id} text={acc.name} />
          ))}
        </Select>
        <Select
          id="accounts_payable_account"
          onChange={handleOnAccountChange('accounts_payable_account', accounts.accounts_payable_account)}
          value={predictSettings.accounts_payable_account?.id}
          error={hasError('accounts_payable_account') ? getError('accounts_payable_account') || undefined : undefined}
          label={intl.formatMessage({
            id: 'predict.accounts_payable_account',
            defaultMessage: 'Accounts payable account'
          })}
          size="large"
          mb={3}
        >
          {accounts.accounts_payable_account.map(acc => (
            <Option key={acc.id} value={acc.id} text={acc.name} />
          ))}
        </Select>
        <div className={styles.checkboxContainer}>
          <Checkbox
            id="vat_disabled"
            onChange={e => handleBooleanChange(e, 'vat_disabled')}
            checked={!predictSettings.vat_disabled}
            label={intl.formatMessage(
              {
                id: 'predict.i_pay_vat_liability',
                defaultMessage: 'I pay {taxName}: liability will be calculated in your forecast'
              },
              { taxName: formatTAXName(currency || undefined, intl) }
            )}
          />
        </div>

        {!predictSettings.vat_disabled && (
          <>
            <div style={{ marginTop: 16 }}>
              <div className="columns is-multiline">
                <div className="column is-6" data-testid="vat-scheme-section">
                  {isFrenchAccounting ? (
                    <Select
                      autoComplete="off"
                      id="vat_scheme"
                      onChange={handleOnChange('vat_scheme')}
                      value={predictSettings.vat_scheme}
                      label={intl.formatMessage(
                        { id: 'predict.vat_scheme', defaultMessage: '{taxName} scheme' },
                        { taxName: formatTAXName(currency || undefined, intl) }
                      )}
                      size="large"
                      mb={0}
                    >
                      {/* This should always be `TVA sur les débits` for French accounting rules (no matter which language is selected) */}
                      <Option
                        value={VAT_SCHEME.ACCRUAL_BASED}
                        text={intl.formatMessage({
                          id: 'predict.fr_excel_accrual_based',
                          defaultMessage: 'TVA sur les débits'
                        })}
                      />
                    </Select>
                  ) : (
                    <Select
                      autoComplete="off"
                      id="vat_scheme"
                      onChange={handleOnChange('vat_scheme')}
                      value={predictSettings.vat_scheme}
                      label={intl.formatMessage(
                        { id: 'predict.vat_scheme', defaultMessage: '{taxName} scheme' },
                        { taxName: formatTAXName(currency || undefined, intl) }
                      )}
                      size="large"
                      mb={0}
                    >
                      <Option
                        value={VAT_SCHEME.ACCRUAL_BASED}
                        text={intl.formatMessage({ id: 'predict.accrual_based', defaultMessage: 'Accrual based' })}
                      />
                      <Option
                        value={VAT_SCHEME.CASH_BASED}
                        text={intl.formatMessage({ id: 'predict.cash_based', defaultMessage: 'Cash based' })}
                      />
                    </Select>
                  )}
                </div>
                <div className="column is-6" data-testid="vat-account-section">
                  <Select
                    id="vat_account"
                    onChange={handleOnAccountChange('vat_account', accounts.vat_account)}
                    value={predictSettings.vat_account?.id ?? ''}
                    error={hasError('vat_account') ? getError('vat_account') || undefined : undefined}
                    label={intl.formatMessage(
                      { id: 'predict.vat_control_account', defaultMessage: '{taxName} control account' },
                      { taxName: formatTAXName(currency || undefined, intl) }
                    )}
                    size="large"
                    mb={0}
                  >
                    {accounts.vat_account.map(acc => (
                      <Option key={acc.id} value={acc.id} text={acc.name} />
                    ))}
                  </Select>
                </div>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <div className="columns is-multiline">
                <div className="column is-6" data-testid="vat-period-section">
                  {isFrenchAccounting ? (
                    <Select
                      id="vat_period_frequency"
                      onChange={handleOnChange('vat_period')}
                      value={predictSettings.vat_period}
                      label={intl.formatMessage(
                        { id: 'predict.vat_frequency', defaultMessage: '{taxName} frequency' },
                        { taxName: formatTAXName(currency || undefined, intl) }
                      )}
                      size="large"
                      mb={0}
                    >
                      <Option
                        value={VAT_PERIOD.MONTHLY}
                        text={intl.formatMessage({ id: 'common.monthly_excel', defaultMessage: 'Monthly' })}
                      />
                      <Option
                        value={VAT_PERIOD.QUARTERLY}
                        text={intl.formatMessage({ id: 'common.quarterly', defaultMessage: 'Quarterly' })}
                      />
                      <Option
                        value={VAT_PERIOD.ANNUALLY}
                        text={intl.formatMessage({ id: 'common.annually', defaultMessage: 'Annually' })}
                      />
                    </Select>
                  ) : (
                    <Select
                      id="vat_period_frequency"
                      onChange={handleOnChange('vat_period')}
                      value={predictSettings.vat_period}
                      label={intl.formatMessage(
                        { id: 'predict.vat_frequency', defaultMessage: '{taxName} frequency' },
                        { taxName: formatTAXName(currency || undefined, intl) }
                      )}
                      size="large"
                      mb={0}
                    >
                      <Option
                        value={VAT_PERIOD.MONTHLY}
                        text={intl.formatMessage({ id: 'common.monthly_excel', defaultMessage: 'Monthly' })}
                      />
                      <Option
                        value={VAT_PERIOD.TWO_MONTHLY}
                        text={intl.formatMessage({ id: 'common.bimonthly', defaultMessage: 'Every 2 months' })}
                      />
                      <Option
                        value={VAT_PERIOD.QUARTERLY}
                        text={intl.formatMessage({ id: 'common.quarterly', defaultMessage: 'Quarterly' })}
                      />
                      <Option
                        value={VAT_PERIOD.SIX_MONTHLY}
                        text={intl.formatMessage({ id: 'common.biannual', defaultMessage: 'Every 6 months' })}
                      />
                      <Option
                        value={VAT_PERIOD.ANNUALLY}
                        text={intl.formatMessage({ id: 'common.annually', defaultMessage: 'Annually' })}
                      />
                    </Select>
                  )}
                </div>
                <div className="column is-6" data-testid="vat-period-end-month-section">
                  <Select
                    autoComplete="off"
                    onChange={handleOnChange('vat_period_end_month')}
                    id="vat_period_end_month"
                    value={String(predictSettings.vat_period_end_month)}
                    label={intl.formatMessage(
                      { id: 'predict.end_of_vat_period', defaultMessage: 'End of {taxName} period' },
                      { taxName: formatTAXName(currency || undefined, intl) }
                    )}
                    size="large"
                    style={{ textTransform: 'capitalize' }}
                    mb={0}
                  >
                    {times(MONTHS_IN_YEAR, (i: number) => (
                      <Option
                        className={styles.capitalized}
                        key={i}
                        value={String(i + 1)}
                        // eslint-disable-next-line no-magic-numbers
                        text={intl.formatDate(new Date(2025, i, 5), { month: 'long' })}
                      />
                    ))}
                  </Select>
                </div>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <div className="columns is-multiline">
                <div className="column is-6" data-testid="vat-rate-section">
                  <UICarbonNumberField
                    type="percentage"
                    clampMax={MAX_PERCENTAGE}
                    clampMin={0}
                    autoComplete="off"
                    onChange={handleOnNumberChange('vat_rate')}
                    onBlur={handleOnNumberBlur('vat_rate', '0.01')}
                    error={hasError('vat_rate') ? getError('vat_rate') || undefined : undefined}
                    id="vat_rate"
                    value={predictSettings.vat_rate.toString()}
                    label={intl.formatMessage(
                      { id: 'predict.tax_name_rate', defaultMessage: '{taxName} rate' },
                      { taxName: formatTAXName(currency || undefined, intl) }
                    )}
                    size="large"
                    mb={0}
                  />
                </div>
                <div className="column is-6" data-testid="vat-payment-due-section" style={{ position: 'relative' }}>
                  <UICarbonNumberField
                    autoComplete="off"
                    type="number"
                    clampMax={isVatPeriodMonthly() ? MAX_VAT_PAYMENT_DAYS_MONTHLY : MAX_PERCENTAGE}
                    error={
                      hasError('vat_period_days')
                        ? getError('vat_period_days') || undefined
                        : showVatPeriodDaysValidationMsg
                        ? intl.formatMessage({
                            id: 'predict.we_cannot_support_monthly_payment',
                            defaultMessage:
                              'We currently cannot support monthly payments made after the following period has closed.'
                          })
                        : undefined
                    }
                    clampMin={1}
                    onChange={handleOnNumberChange('vat_period_days')}
                    onBlur={handleOnNumberBlur('vat_period_days', '1')}
                    id="vat_period_days"
                    value={predictSettings.vat_period_days.toString()}
                    label={intl.formatMessage(
                      { id: 'predict.vat_payment_due_after', defaultMessage: '{taxName} payment due after' },
                      { taxName: formatTAXName(currency || undefined, intl) }
                    )}
                    size="large"
                    mb={0}
                  />
                  {hasError('vat_period_days') ? null : (
                    <div style={{ position: 'absolute', right: 24, top: 54, zIndex: 2 }}>
                      <FormattedMessage id="common.Days" defaultMessage="Days" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AssistantSidePanelFormContainer>
  );
};
